// Client documents API (TDDS-3384) on the Terraform-managed bhp-client-portal gateway.
// Calls must carry the Cognito ID token: the backend reads custom:portalUserId from it
// to prove the caller owns the party/matter/file.
const GATEWAY_API_BASE_URL =
  process.env.REACT_APP_GATEWAY_API_BASE_URL ||
  process.env.REACT_APP_CONTENT_API_BASE_URL;

export const DOCUMENT_CATEGORIES = [
  { key: "ID_DOCUMENT", label: "Documento de identidade" },
  { key: "POWER_OF_ATTORNEY", label: "Procuração" },
  { key: "PHOTOGRAPHIC_EVIDENCE", label: "Prova de vida" },
  { key: "FEE_AGREEMENT", label: "Contrato de honorários" },
  { key: "PROOF_OF_ADDRESS", label: "Comprovante de residência atual" },
  { key: "OTHER", label: "Outros documentos" },
];

export const categoryLabel = (key) =>
  DOCUMENT_CATEGORIES.find((c) => c.key === key)?.label || "Sem categoria";

// TDDS-3492: no banner/dashboard alert pressing clients for missing "important" documents for now.
// Flip to true once those documents become genuinely required.
export const PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS = false;

// Labels of mandatory categories with no documents in a GET /documents response.
export const missingMandatoryLabels = (resp) =>
  (Array.isArray(resp?.categories) ? resp.categories : [])
    .filter((g) => g.mandatory && !(g.documents || []).length)
    .map((g) => categoryLabel(g.key));

const documentsUrl = (partyId, matterId, suffix = "") =>
  `${GATEWAY_API_BASE_URL}/clients/${encodeURIComponent(
    partyId,
  )}/matters/${encodeURIComponent(matterId)}/documents${suffix}`;

const request = async (url, idToken, options = {}) => {
  if (!idToken) throw new Error("Sessão expirada. Faça login novamente.");

  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${idToken}`,
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
  });

  const text = await response.text().catch(() => "");
  let body = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const error = new Error(body?.message || `Error ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return body;
};

export const fetchClientDocuments = (idToken, partyId, matterId) =>
  request(documentsUrl(partyId, matterId), idToken, { method: "GET" });

export const fetchClientDocumentDownload = (idToken, partyId, matterId, fileId) =>
  request(
    documentsUrl(partyId, matterId, `/${encodeURIComponent(fileId)}/download`),
    idToken,
    { method: "GET" },
  );

export const classifyClientDocument = (
  idToken,
  partyId,
  matterId,
  fileId,
  category,
) =>
  request(
    documentsUrl(partyId, matterId, `/${encodeURIComponent(fileId)}`),
    idToken,
    { method: "PATCH", body: JSON.stringify({ category }) },
  );

export const UPLOAD_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.doc,.docx";
export const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;

export const startClientDocumentUpload = (idToken, partyId, matterId, file, category) =>
  request(documentsUrl(partyId, matterId), idToken, {
    method: "POST",
    body: JSON.stringify({
      fileName: file.name,
      fileSize: file.size,
      ...(category ? { category } : {}),
    }),
  });

export const completeClientDocumentUpload = (idToken, partyId, matterId, fileId) =>
  request(
    documentsUrl(partyId, matterId, `/${encodeURIComponent(fileId)}/complete`),
    idToken,
    { method: "POST" },
  );

export const putFileToSignedUrl = (url, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed with status ${xhr.status}`));
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(file);
  });
